import type { Venue } from '@lunit/shared-types';

/**
 * Ссылки «поделиться» (приглашение, подарок) и разбор входящих ссылок — для обоих входов в одном месте, чтобы
 * формат сборки и разбора не разъезжались. Ссылка ведёт в тот вход, из которого ею поделились:
 *   - мини-апп: https://t.me/<бот>/<app>?startapp=<slug>--ref<код> | <slug>--gift<код>;
 *   - PWA:      https://<сайт PWA>/<slug>?ref=<код> | ?gift=<код>.
 * Код приглашения — referralCode профиля (6 символов A-Z/2-9) либо legacy telegramId (только цифры): бэкенд
 * принимает оба. Telegram пропускает в startapp только [A-Za-z0-9_-], поэтому код — строго буквы и цифры.
 */

const REFERRAL_CODE = /^[A-Za-z0-9]{1,32}$/;
const GIFT_CLAIM_CODE = /^[A-Za-z0-9_-]{1,64}$/;

export interface StartParams {
  venueSlug: string | null;
  referralCode: string | null;
  giftClaimCode: string | null;
}

const EMPTY: StartParams = { venueSlug: null, referralCode: null, giftClaimCode: null };

/** Нормализованный код приглашения или null, если он не годится для ссылки. */
export function normalizeReferralCode(code: string | null | undefined): string | null {
  const c = (code ?? '').trim();
  return REFERRAL_CODE.test(c) ? c : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Мини-апп (Telegram startapp)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Разбор Telegram start_param мини-аппа.
 *
 * Форматы:
 * - "venueSlug" — только заведение;
 * - "venueSlug--ref<код>" — заведение + приглашение (код — referralCode или legacy telegramId);
 * - "venueSlug--gift<код>" — заведение + подарок по ссылке;
 * - "venueSlug_ref<telegramId>" — старый формат приглашения (подчёркивание);
 * - "<telegramId>" — совсем старый формат: только приглашение, заведение — из других источников.
 */
export function parseTelegramStartParam(startParam: string | null | undefined): StartParams {
  if (!startParam) {
    return { ...EMPTY };
  }

  // Новый формат приглашения: двойной дефис (в слаге бывают подчёркивания). Код — буквы и цифры:
  // собственный код клиента или legacy telegramId.
  const ref = startParam.match(/^(.+)--ref([A-Za-z0-9]+)$/);
  if (ref) {
    return { venueSlug: ref[1], referralCode: ref[2], giftClaimCode: null };
  }

  const gift = startParam.match(/^(.+)--gift(.+)$/);
  if (gift) {
    return { venueSlug: gift[1], referralCode: null, giftClaimCode: gift[2] };
  }

  const legacyRef = startParam.match(/^(.+)_ref(\d+)$/);
  if (legacyRef) {
    return { venueSlug: legacyRef[1], referralCode: legacyRef[2], giftClaimCode: null };
  }

  if (/^\d+$/.test(startParam)) {
    return { venueSlug: null, referralCode: startParam, giftClaimCode: null };
  }

  return { venueSlug: startParam, referralCode: null, giftClaimCode: null };
}

/** Приглашение в мини-апп заведения: t.me/<бот заведения>/<app>?startapp=<slug>--ref<код>. */
export function telegramInviteLink(
  venue: Pick<Venue, 'slug' | 'telegramBot' | 'telegramMiniApp'>,
  referralCode: string
): string | null {
  const code = normalizeReferralCode(referralCode);
  const bot = venue.telegramBot?.trim();
  if (!code || !bot || !venue.slug) return null;
  const app = venue.telegramMiniApp?.trim() || 'app';
  return `https://t.me/${bot}/${app}?startapp=${venue.slug}--ref${code}`;
}

/** Подарок в мини-апп: t.me/<бот>/app?startapp=<deepLink> (deepLink выдаёт сервер: <slug>--gift<код>). */
export function telegramGiftLink(deepLink: string, botUsername: string): string | null {
  if (!deepLink || !botUsername) return null;
  return `https://t.me/${botUsername}/app?startapp=${deepLink}`;
}

// ---------------------------------------------------------------------------------------------------------------
// PWA (сайт заведения)
// ---------------------------------------------------------------------------------------------------------------

function pwaVenueUrl(origin: string, slug: string): string {
  return `${origin.replace(/\/+$/, '')}/${encodeURIComponent(slug)}`;
}

/** Приглашение в PWA заведения: <origin>/<slug>?ref=<код> — регистрация в PWA передаёт код на сервер. */
export function pwaInviteLink(origin: string, slug: string, referralCode: string): string | null {
  const code = normalizeReferralCode(referralCode);
  if (!code || !origin || !slug) return null;
  return `${pwaVenueUrl(origin, slug)}?ref=${code}`;
}

/** Подарок в PWA заведения: <origin>/<slug>?gift=<код> — после входа PWA забирает подарок по коду. */
export function pwaGiftLink(origin: string, slug: string, claimCode: string): string | null {
  if (!origin || !slug || !GIFT_CLAIM_CODE.test(claimCode)) return null;
  return `${pwaVenueUrl(origin, slug)}?gift=${encodeURIComponent(claimCode)}`;
}

/** Разбор ?ref= и ?gift= входящей ссылки PWA. */
export function parsePwaEntryParams(search: string): Pick<StartParams, 'referralCode' | 'giftClaimCode'> {
  const params = new URLSearchParams(search);
  const gift = (params.get('gift') ?? '').trim();
  return {
    referralCode: normalizeReferralCode(params.get('ref')),
    giftClaimCode: GIFT_CLAIM_CODE.test(gift) ? gift : null,
  };
}
